import React, { useEffect, useId, useRef } from "react";
import { Btn, Field, NoticeState, Overline } from "../../../ForBrands/ui/BrandUI";
import { dataStatusNotice, teamsDataStatus } from "../allocationModel";
import { cascadeFieldSpecs, spacedSlash, teamDisplayName, useCascadeFields } from "../hooks/useCascadeFields";
import { teamsStatusText } from "../hooks/useTeamCascade";
import { CascadeSelect, SearchField } from "./allocationUi";
import TeamList, { teamCountLabel } from "./TeamList";

const NARROWS = "Optional. Narrows the team list.";

// A step: "STEP N" and its title, an optional hint, then the fields. The
// rail rules the steps apart; the phone sheet spaces them 24px apart with no
// rules and 12px inside each step (2420:2373); the bar sets them in a grid,
// the team step two columns wide (and two rows tall from 1280px).
const STEP_CLASS = {
  rail: "flex flex-col gap-2 border-b border-bm-mutedbg py-5 last:border-b-0",
  sheet: "flex flex-col gap-3",
  bar: "flex min-w-0 flex-col gap-2",
};
const RailStep = ({ step, title, hint, variant, wide = false, children }) => (
  <section aria-label={`Step ${step} ${title}`} className={`${STEP_CLASS[variant] || STEP_CLASS.rail}${variant === "bar" && wide ? " sm:col-span-2 xl:row-span-2" : ""}`}>
    <div className="flex items-baseline gap-2">
      <span className="bm-overline !text-bm-tertiary">Step {step}</span>
      <span className="bm-h-xs text-bm-ink">{title}</span>
    </div>
    {hint && <p className="bm-body-s text-bm-muted">{hint}</p>}
    {children}
  </section>
);

// The team step sits right after the tier: a tier alone lists teams, and
// the governing body, league and conference below only narrow the list.
// On the phone sheet the search is 56px and the list 280px tall
// (2420:2447), and picking a team calls `onPick` as well. A list served from
// a provider's saved copy (the feed is paused, so only the teams with saved
// stats are listed) says so above the teams.
const TeamPicker = ({ cascade, sheet, onPick }) => {
  const { filters, setFilters, teams, filteredTeams, isLoadingTeams, teamsError, teamsErrorText, retryTeams, selectedTeam, setSelectedTeam, scopeName } = cascade;
  const anyLeague = filters.body?.anyLeague === true && (filters.teamSearch || "").trim().length < 2;
  let status = teamCountLabel(filteredTeams.length, scopeName);
  if (isLoadingTeams) status = "Loading teams...";
  else if (teamsError) status = teamsStatusText(teamsErrorText, "Teams did not load for this selection.");
  else if (anyLeague) status = "Type at least two letters of the team name to search every league.";
  const notice = !isLoadingTeams && !teamsError ? dataStatusNotice(teamsDataStatus(teams)) : "";
  const size = sheet ? "lg" : "md";
  const select = onPick
    ? (t) => {
        setSelectedTeam(t);
        onPick(t);
      }
    : setSelectedTeam;
  return (
    <div className={sheet ? "flex flex-col gap-3" : "flex flex-col gap-2"}>
      <SearchField value={filters.teamSearch || ""} onChange={(v) => setFilters({ ...filters, teamSearch: v })} placeholder="Search any team…" label="Search any team" size={size} />
      <span role={teamsError ? "alert" : undefined} className={`bm-body-s ${teamsError ? "text-bm-critical" : "text-bm-tertiary"}`}>
        {status}
      </span>
      {notice && (
        <NoticeState role="status" className="bm-body-s !px-3 !py-2">
          {notice}
        </NoticeState>
      )}
      {teamsError && !isLoadingTeams && (
        <Btn variant="secondary" size={sheet ? "lg" : "sm"} className="self-start" onClick={retryTeams}>
          Try again
        </Btn>
      )}
      {!isLoadingTeams && filteredTeams.length > 0 && <TeamList teams={filteredTeams} selectedTeam={selectedTeam} onSelect={select} className={sheet ? "h-[280px]" : "h-[240px]"} size={size} />}
      {!isLoadingTeams && !teamsError && !anyLeague && teams.length === 0 && <p className="bm-body-s text-bm-muted">No teams found for this selection.</p>}
    </div>
  );
};

// The picked scope in one line, for the folded bar: "Basketball · Men /
// Boys · College · Clark Cougars".
const selectionSummary = (filters, team) =>
  [filters.sport?.name, spacedSlash(filters.gender?.name), filters.tier?.name, teamDisplayName(team)].filter(Boolean).join(" · ");

// Admin CacheShare™ rail (Figma 03 · Selection filters). `variant="sheet"`
// is its phone form (2420:2373), shown inside the full-screen Selection
// filters dialog: no aside or rules, the "CacheShare" overline moves to the
// dialog header, 56px fields with Label/L labels, and `onTeamPick` runs once
// a team is picked (the page closes the dialog then, D-cs6).
//
// `variant="bar"` is the Admin Mode form from 640px up: the same steps in a
// bar above the workspace instead of a 320px column beside it, so the
// revenue share and NIL tables get the full width and sit side by side as
// in Coach Mode. `open` folds it to its head and a summary of the pick;
// `onToggle` (given once a team is picked) adds the Change selection /
// Hide filters button, which takes the focus when a pick folds the bar.
const CacheShareRail = ({ cascade, variant = "rail", onTeamPick, open = true, onToggle }) => {
  const { filters, setFilters } = cascade;
  const f = useCascadeFields(filters, setFilters);
  const spec = cascadeFieldSpecs(f, filters);
  const sheet = variant === "sheet";
  const bar = variant === "bar";
  const size = sheet ? "lg" : "md";
  const bodyId = useId();
  const toggleId = useId();
  const wasOpen = useRef(open);
  useEffect(() => {
    if (bar && wasOpen.current && !open) document.getElementById(toggleId)?.focus();
    wasOpen.current = open;
  }, [bar, open, toggleId]);
  const fieldClass = sheet ? "!gap-2" : undefined;
  const optional = [spec.region, spec.body, spec.subDivision, spec.classDivision, spec.conference].filter(Boolean);
  const steps = [
    {
      title: "Sport",
      body: (
        <>
          <Field label="Sport" className={fieldClass}>
            <CascadeSelect spec={spec.sport} size={size} />
          </Field>
          {spec.gender && (
            <Field label="Gender" className={fieldClass}>
              <CascadeSelect spec={spec.gender} size={size} />
            </Field>
          )}
        </>
      ),
    },
    spec.tier && { title: "Tier", hint: "Selecting a new tier resets the filters below.", body: <CascadeSelect spec={spec.tier} size={size} /> },
    filters.tier && { title: "Team", wide: true, body: <TeamPicker cascade={cascade} sheet={sheet} onPick={onTeamPick} /> },
    ...optional.map((s) => ({
      title: s.label,
      hint: s === spec.conference ? "Optional. Skip to see every NCAA program." : NARROWS,
      body: <CascadeSelect spec={s} size={size} />,
    })),
  ].filter(Boolean);

  const stepList = steps.map((s, i) => (
    <RailStep key={s.title} step={i + 1} title={s.title} hint={s.hint} variant={variant} wide={s.wide}>
      {s.body}
    </RailStep>
  ));

  if (bar) {
    const summary = selectionSummary(filters, cascade.selectedTeam);
    return (
      <aside aria-label="Build your selection" className="min-w-0 rounded-[12px] border border-bm-border bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="flex min-w-0 flex-col gap-1">
            <Overline brand>CacheShare</Overline>
            <h2 className="bm-h-m text-bm-ink">Build your selection</h2>
            {!open && summary && <p className="break-words bm-body-s text-bm-muted">{summary}</p>}
          </div>
          {onToggle && (
            <Btn id={toggleId} variant="secondary" aria-expanded={open} aria-controls={open ? bodyId : undefined} onClick={onToggle}>
              {open ? "Hide filters" : "Change selection"}
            </Btn>
          )}
        </div>
        {open && (
          <div id={bodyId} className="grid gap-x-6 gap-y-5 border-t border-bm-mutedbg px-5 py-5 sm:grid-cols-2 xl:grid-cols-4">
            {stepList}
          </div>
        )}
      </aside>
    );
  }

  if (sheet) {
    return (
      <div className="flex flex-col gap-6">
        <h3 className="bm-h-m text-bm-ink">Build your selection</h3>
        {stepList}
      </div>
    );
  }

  return (
    <aside className="flex w-full shrink-0 flex-col lg:w-[320px] lg:border-r lg:border-bm-border">
      <div className="border-b border-bm-mutedbg px-6 pb-5 pt-6">
        <Overline brand>CacheShare</Overline>
        <h2 className="bm-h-m text-bm-ink">Build your selection</h2>
      </div>
      <div className="px-6">{stepList}</div>
    </aside>
  );
};

export default CacheShareRail;
